import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Logo from './imagens/logo_original.png';
import HeroImage from './imagens/hero-mulher.png';
import Card1 from './imagens/card1.jpg';
import Card2 from './imagens/card2.jpg';
import Card3 from './imagens/card3.jpg';
import Card4 from './imagens/card4.jpg';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ── Feature panel (idêntico ao login) ── */
function FeaturePanel() {
  const cards = [
    { src: Card1, rotate: -12, top: '5%',   left: '-12%',  delay: 0.3 },
    { src: Card2, rotate: 10,  top: '2%',   right: '-30%', delay: 0.45 },
    { src: Card3, rotate: -6,  top: '45%',  left: '-13%',  delay: 0.6 },
    { src: Card4, rotate: 8,   top: '42%',  right: '-30%', delay: 0.75 },
  ];

  return (
    <section className="relative flex h-full flex-col items-center justify-center overflow-inherit py-8 lg:py-0">
      <div className="relative mx-auto w-full max-w-sm">
        {cards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.7, rotate: card.rotate * 0.5 }}
            animate={{ opacity: 1, scale: 1, rotate: card.rotate }}
            transition={{ duration: 0.6, delay: card.delay, type: 'spring', stiffness: 80 }}
            className="absolute z-0"
            style={{
              top: card.top,
              ...(card.left ? { left: card.left } : {}),
              ...(card.right ? { right: card.right } : {}),
              width: '42%',
            }}
          >
            <img
              src={card.src}
              alt=""
              className="w-full rounded-xl shadow-lg"
              style={{
                border: '3px solid rgba(255,255,255,0.6)',
                boxShadow: '0 8px 30px rgba(63, 4, 92, 0.2)',
              }}
            />
          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative z-10"
        >
          <img
            src={HeroImage}
            alt="Mulher acessando a plataforma VídeoOferta no celular"
            className="mx-auto w-full max-w-xs drop-shadow-2xl sm:max-w-sm"
            style={{
              filter: 'drop-shadow(0 20px 40px rgba(63, 4, 92, 0.18))',
              maskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 81%, transparent 100%)'
            }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="absolute z-20"
          style={{ top: '61%', left: '20%' }}
        >
          <h1 className="hero-title">Crie ofertas em vídeo</h1>
        </motion.div>

        {/* Badge: Templates prontos */}
        <motion.div
          initial={{ opacity: 0, x: -20, y: -10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5, type: 'spring', stiffness: 120 }}
          className="absolute left-0 top-6 z-30 sm:-left-4 lg:-left-6"
        >
          <div className="flex items-center gap-2.5 rounded-2xl border border-flow-border bg-white px-4 pt-[2px] pb-[2px] shadow-lg">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-flow-brand-soft text-flow-brand">
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path d="M4 5h16v14H4z" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M7 9h10M7 13h6" stroke="currentColor" strokeWidth="2" />
              </svg>
            </span>
            <span className="text-sm font-semibold text-flow-ink">Templates prontos</span>
          </div>
        </motion.div>

        {/* Badge: Baixe e publique */}
        <motion.div
          initial={{ opacity: 0, x: 20, y: 10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, type: 'spring', stiffness: 120 }}
          className="absolute right-0 top-6 md:top-auto md:bottom-16 z-30 lg:-right-6"
        >
          <div className="flex items-center gap-2.5 rounded-2xl border border-flow-border bg-white px-4 pt-[2px] pb-[2px] shadow-lg">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-flow-brand-soft text-flow-brand">
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="text-sm font-semibold text-flow-ink">Baixe e publique</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Registration form ── */
function RegisterForm({ loading, onSubmit, message, setMessage }) {
  const [form, setForm] = useState({ nome: '', email: '', senha: '', telefone: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ nome: false, email: false, senha: false });
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => {
    const next = {};
    if (!form.nome) {
      next.nome = 'Informe seu nome.';
    }
    if (!form.email) {
      next.email = 'Informe seu e-mail.';
    } else if (!emailRegex.test(form.email)) {
      next.email = 'Digite um e-mail válido.';
    }
    if (!form.senha) {
      next.senha = 'Informe sua senha.';
    } else if (form.senha.length < 6) {
      next.senha = 'Use ao menos 6 caracteres.';
    }
    return next;
  }, [form.nome, form.email, form.senha]);

  const isFormValid = Object.keys(errors).length === 0;
  const showNomeError = (touched.nome || submitted) && errors.nome;
  const showEmailError = (touched.email || submitted) && errors.email;
  const showSenhaError = (touched.senha || submitted) && errors.senha;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!isFormValid) {
      setMessage({ type: 'error', text: 'Revise os campos para continuar.' });
      return;
    }
    setMessage(null);
    onSubmit(form);
  };

  const inputClass = (hasError) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-sm text-flow-ink placeholder-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-flow-brand ${
      hasError ? 'border-red-400/70 focus-visible:ring-red-400' : 'border-flow-border focus-visible:border-flow-brand'
    }`;

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      {/* Nome */}
      <div className="space-y-2">
        <label htmlFor="nome" className="text-sm font-medium text-flow-ink">
          Nome
        </label>
        <input
          id="nome"
          name="nome"
          type="text"
          autoComplete="name"
          className={inputClass(showNomeError)}
          placeholder="Seu nome completo"
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
          onBlur={() => setTouched((p) => ({ ...p, nome: true }))}
          aria-invalid={Boolean(showNomeError)}
          required
        />
        {showNomeError && <p className="text-xs text-red-300">{errors.nome}</p>}
      </div>

      {/* E-mail */}
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-flow-ink">
          E-mail
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          className={inputClass(showEmailError)}
          placeholder="voce@exemplo.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          onBlur={() => setTouched((p) => ({ ...p, email: true }))}
          aria-invalid={Boolean(showEmailError)}
          required
        />
        {showEmailError && <p className="text-xs text-red-300">{errors.email}</p>}
      </div>

      {/* Telefone */}
      <div className="space-y-2">
        <label htmlFor="telefone" className="text-sm font-medium text-flow-ink">
          Telefone
        </label>
        <PhoneInput
          id="telefone"
          defaultCountry="BR"
          value={form.telefone}
          onChange={(value) => setForm({ ...form, telefone: value || '' })}
          international
          countryCallingCodeEditable={false}
          className={inputClass(false)}
        />
      </div>

      {/* Senha */}
      <div className="space-y-2">
        <label htmlFor="senha" className="text-sm font-medium text-flow-ink">
          Senha
        </label>
        <div className="relative">
          <input
            id="senha"
            name="senha"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            className={`${inputClass(showSenhaError)} pr-12`}
            placeholder="••••••••"
            value={form.senha}
            onChange={(e) => setForm({ ...form, senha: e.target.value })}
            onBlur={() => setTouched((p) => ({ ...p, senha: true }))}
            aria-invalid={Boolean(showSenhaError)}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="absolute inset-y-0 right-3 flex items-center text-xs font-medium text-flow-muted transition hover:text-flow-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
            aria-pressed={showPassword}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showPassword ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {showSenhaError && <p className="text-xs text-red-300">{errors.senha}</p>}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!isFormValid || loading}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-flow-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand disabled:cursor-not-allowed disabled:opacity-60"
        aria-disabled={!isFormValid || loading}
      >
        {loading ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
            Cadastrando...
          </>
        ) : (
          'Criar conta'
        )}
      </button>

      {/* Message */}
      {message && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            message.type === 'error'
              ? 'border-red-400/40 bg-red-500/10 text-red-600'
              : 'border-flow-brand/30 bg-flow-brand-soft text-flow-brand'
          }`}
          role={message.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {message.text}
        </div>
      )}

      <p className="text-center text-sm text-flow-muted">
        Já possui uma conta?{' '}
        <Link
          to="/entrar"
          className="font-semibold text-flow-brand transition hover:text-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
        >
          Entrar
        </Link>
      </p>
    </form>
  );
}

/* ── Page ── */
function CadastrarPage() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const abortRef = useRef(null);

  const abortInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const handleSubmit = async (form) => {
    abortInFlight();
    setMessage(null);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('https://videooferta.com/api/cadastrar.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.success) {
        sessionStorage.setItem('user', JSON.stringify(data.user));
        window.location.href = `/?id=${data.video.id_publico}`;
      } else {
        setMessage({ type: 'error', text: data.error || 'Não foi possível criar a conta.' });
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setMessage({ type: 'error', text: 'Erro ao conectar com o servidor.' });
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  return (
    <div className="relative min-h-screen bg-dot-grid overflow-hidden">
      <main className="relative mx-auto flex min-h-screen max-w-6xl flex-col gap-10 px-6 py-12 lg:flex-row lg:items-center">
        {/* Feature panel — left on desktop */}
        <div className="order-2 w-full lg:order-1 lg:w-1/2">
          <FeaturePanel />
        </div>

        {/* Form card — right on desktop */}
        <div className="order-1 w-full lg:order-2 lg:w-1/2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="mx-auto w-full max-w-md rounded-2xl border border-flow-border bg-white p-6 sm:p-8"
          >
            <div className="mb-6 flex items-center justify-center">
              <img src={Logo} alt="VídeoOferta" className="h-12 w-auto" />
            </div>

            <div className="mb-6 space-y-2">
              <h2 className="text-2xl font-semibold text-flow-ink">Criar conta</h2>
              <p className="text-sm text-flow-muted">
                Cadastre-se e comece a criar vídeos de ofertas agora.
              </p>
            </div>

            <RegisterForm
              loading={loading}
              onSubmit={handleSubmit}
              message={message}
              setMessage={setMessage}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default CadastrarPage;
